<template>
  <v-row justify="center" v-if="isDialogOpen">
    <v-dialog
      v-model="isDialogOpen"
      hide-overlay
      :transition="false"
      origin="center top"
      fullscreen
      :retain-focus="false"
      @click:outside="getClosed(true)">
      <v-card class="day-dialog">
        <v-toolbar class="day-schedule-header">
          <v-btn icon dark @click="getClosed(true)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title
            ><span class="text-uppercase" v-if="daySelected != null">
              {{ $t("daySchedule.title") }} -
              {{ $dayjs(daySelected).format("DD MMMM YYYY") }}
            </span>
            <span class="text-uppercase" v-else>{{
              $t("calendar.onDemand")
            }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>

        <div>
          <v-container class="pt-10">
            <v-row>
              <v-col
                cols="10"
                sm="4"
                md="2"
                offset-md="3"
                offset-sm="2"
                offset="1">
                <v-autocomplete
                  v-model="typeFilter"
                  :items="types"
                  dense
                  autocomplete="chrome-off"
                  hide-details="auto"
                  label="Type: "
                  outlined>
                </v-autocomplete>
              </v-col>
              <v-col cols="10" sm="4" offset="1" offset-sm="0">
                <v-text-field
                  v-model="searchText"
                  dense
                  autocomplete="chrome-off"
                  :label="$t('daySchedule.search') + ':'"
                  :placeholder="$t('daySchedule.searchPlaceholder')"
                  outlined>
                </v-text-field>
              </v-col>
            </v-row>
          </v-container>
          <div
            v-if="filteredSessions && filteredSessions.length > 0"
            :key="typeFilter + searchText">
            <div
              class="time-list-container"
              v-for="(session, i) in filteredSessions"
              :key="'time-list-container' + i">
              <div
                v-if="
                  session &&
                  session.presentations != null &&
                  session.presentations.length > 0
                ">
                <h1
                  class="text-center"
                  v-if="$dayjs(session.start).get('hour') != 0">
                  {{ getTime(session.start, session.end) }}
                  <v-icon v-if="session.start != null"
                    >mdi-clock-outline</v-icon
                  >
                  <span
                    v-if="
                      getTime(session.start, session.end) !=
                      $t('calendar.onDemand')
                    ">
                    {{ $t("daySchedule.timezone") }}</span
                  >
                </h1>
                <p class="text-center" v-else>{{ $t("calendar.onDemand") }}</p>
              </div>
              <v-list v-if="session">
                <v-list-item
                  v-for="(presentation, x) in session.presentations"
                  :key="'pre' + x">
                  <session-card :session="presentation"></session-card>
                </v-list-item>
              </v-list>
            </div>
          </div>
          <div v-if="sessionsPerTypeSelectedIsLoading && sessionTypeSelected">
            <v-skeleton-loader
              class="mx-auto"
              max-width="1300"
              type="article, article"></v-skeleton-loader>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import SessionCard from "./SessionCard";
import cloneDeep from "lodash/cloneDeep";

export default {
  name: "DayScheduleDialog",
  data() {
    return {
      notifications: false,
      sound: true,
      widgets: false,
      sessionsOfDaySelected: null,
      searchText: "",
      searchTextDebounce: null,
      searchTextDebounceTxt: "",
      typeFilter: this.$t("daySchedule.all"),
    };
  },
  props: {
    isDialogOpen: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    types: {
      get() {
        let sessionType = this.sessionsPerDayOfWeekSelected.flatMap((session) =>
          session.presentations.flatMap((presentation) => {
            if (presentation.sessionType == "")
              return this.$t("daySchedule.noType");

            return presentation.sessionType;
          })
        );

        let distinctSessionType = [...new Set(sessionType)].sort();
        distinctSessionType.unshift(this.$t("daySchedule.all"));

        return distinctSessionType;
      },
    },
    daySelected: {
      get() {
        return this.$store.getters["getDaySelected"];
      },
      set(day) {
        return this.$store.dispatch("changeDaySelected", day);
      },
    },
    timeSlotSelected: {
      get() {
        return this.$store.getters["getTimeSlotSelected"];
      },
      set(timeslot) {
        return this.$store.dispatch("changeTimeSlotSelected", timeslot);
      },
    },
    sessionTypeSelected: {
      get() {
        return this.$store.getters["getSessionTypeSelected"];
      },
      set(timeslot) {
        return this.$store.dispatch("changeSessionTypeSelected", timeslot);
      },
    },
    sessionsPerDayOfWeekSelected() {
      return this.$store.getters["getSessionsPerDayOfWeekSelected"];
    },
    sessionsPerTypeSelectedIsLoading() {
      return this.$store.getters["getSessionsPerTypeSelectedIsLoading"];
    },
    sessionsPerTypeSelected() {
      return this.$store.getters["getSessionsPerTypeSelected"];
    },
    filteredSessions() {
      let sessions = cloneDeep(this.sessionsPerDayOfWeekSelected);

      if (sessions != null) {
        if (
          this.timeSlotSelected &&
          this.timeSlotSelected != "" &&
          this.timeSlotSelected != null
        ) {
          sessions = sessions.filter(
            (x) =>
              x.start >= this.timeSlotSelected[0] &&
              x.end <= this.timeSlotSelected[1]
          );
        }

        for (let session of sessions) {
          if (session != undefined) {
            if (this.typeFilter != this.$t("daySchedule.all")) {
              let selectedType = this.typeFilter;
              if (selectedType == this.$t("daySchedule.noType"))
                selectedType = "";
              session.presentations = session.presentations.filter(
                (presentation) => presentation.sessionType == selectedType
              );
            }
            if (this.searchTextDebounceTxt.length > 0) {
              session.presentations = session.presentations.filter(
                (presentation) =>
                  presentation.name.toLowerCase().includes(this.searchTextDebounceTxt.toLowerCase()) || 
                  (presentation.speakers.length > 0 && presentation.speakers.map(v => v.toLowerCase()).some(speaker => speaker.includes(this.searchTextDebounceTxt.toLowerCase())))
              );
            }
          }
        }
      }

      return sessions;
    },
  },
  methods: {
    async fetchSessionsPerTypeSelected(params) {
      await this.$store.dispatch("fetchSessionsPerTypeSelected", params);
    },
    getSessionsOfDaySelected() {
      if (
        this.sessionTypeSelected != null &&
        this.sessionTypeSelected != "" &&
        this.sessionTypeSelected != undefined
      ) {
        this.sessionsOfDaySelected =
          this.sessionsPerTypeSelected[this.sessionTypeSelected];
      } else {
        if (
          this.sessionsPerDayOfWeekSelected &&
          this.sessionsPerDayOfWeekSelected != "" &&
          this.sessionsPerDayOfWeekSelected.length > 0
        ) {
          let x = this.sessionsPerDayOfWeekSelected[0];
          this.sessionsOfDaySelected = x;
        }
      }
    },
    getClosed(b) {
      document.documentElement.classList.remove("remove-sidebar");
      this.$emit("closed", b);
      this.daySelected = "";
      this.timeSlotSelected = null;
      this.typeFilter = this.$t("daySchedule.all");
      this.searchText = "";
    },
    getTime(start, end) {
      if (this.$dayjs(start).get("hour") == 0 || end == null) {
        return this.$t("calendar.onDemand");
      } else {
        return (
          this.$dayjs(start).format("HH:mm") +
          " - " +
          this.$dayjs(end).format("HH:mm")
        );
      }
    },
  },
  components: {
    "session-card": SessionCard,
  },
  watch: {
    daySelected: {
      handler() {
        this.getSessionsOfDaySelected();
      },
    },
    searchText: {
      handler(res) {
        if (this.searchTextDebounce != null) {
          clearTimeout(this.searchTextDebounce);
        }
        this.searchTextDebounce = setTimeout(() => {
          this.searchTextDebounceTxt = res;
        }, 333);
      },
    },
  },
};
</script>
